import React, { useCallback, useEffect, useState } from 'react';
import { Search, X, FileSpreadsheet } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import Pagination from '../../common/Pagination';
import Skeleton from '../../common/Skeleton';
import NoDataFound from '../../common/NoDataFound';
import FileAnalyticsAPI from '../fileAnalyticsService';

interface DataPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasetId?: string | null;
  /** Preview the source's Processed (Silver) snapshot — GET /sources/{id}/preview */
  sourceId?: number | string | null;
  /** Preview the source's Raw (Bronze) layer — GET /sources/{id}/preview-bronze */
  bronzeSourceId?: number | string | null;
  /**
   * Preview what a rule matched, read from that run's own output file —
   * GET /runs/{id}/preview. Detection runs record the file they wrote but no
   * dataset row, so this is the only way to open their findings.
   */
  runId?: number | string | null;
  title?: string;
  onDownload?: () => void;
  downloading?: boolean;
}

interface Preview { columns: string[]; rows: any[][]; total: number; page: number; search: string; pageSize: number; }

const DataPreviewModal: React.FC<DataPreviewModalProps> = ({
  isOpen, onClose, datasetId, sourceId, bronzeSourceId, runId, title, onDownload, downloading,
}) => {
  const [pv, setPv] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async (page: number, term: string, size: number) => {
    if (!datasetId && sourceId == null && bronzeSourceId == null && runId == null) return;
    setLoading(true);
    try {
      const data = datasetId
        ? await FileAnalyticsAPI.previewDataset(datasetId, size, page * size, term || undefined)
        : runId != null
          ? await FileAnalyticsAPI.previewRun(runId, size, page * size, term || undefined)
          : bronzeSourceId != null
            ? await FileAnalyticsAPI.previewSourceBronze(bronzeSourceId, size, page * size, term || undefined)
            : await FileAnalyticsAPI.previewSourceSnapshot(sourceId!, size, page * size, term || undefined);
      setPv({ ...data, page, search: term, pageSize: size });
    } catch {
      setPv(null);
    } finally {
      setLoading(false);
    }
  }, [datasetId, sourceId, bronzeSourceId, runId]);

  useEffect(() => {
    if (isOpen && (datasetId || sourceId != null || bronzeSourceId != null || runId != null)) {
      setSearch(''); setPageSize(25); load(0, '', 25);
    }
  }, [isOpen, datasetId, sourceId, bronzeSourceId, runId, load]);

  const hasRows = !loading && pv && pv.rows.length > 0;
  const isEmpty = !loading && (!pv || pv.rows.length === 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>{title || 'Preview'}</Modal.Header>
      <Modal.Body className="flex flex-col">

        {/* Toolbar */}
        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center" style={{ position: 'relative' }}>
              <FormControl.Input
                type="text"
                placeholder="Search all columns…"
                value={search}
                onChange={(e: any) => { setSearch(e.target.value); load(0, e.target.value, pageSize); }}
                leadingIcon={<Search size={13} />}
                className="filter-search"
                data-testid="INP_fa_preview_search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); load(0, '', pageSize); }}
                  aria-label="Clear search"
                  style={{
                    position: 'absolute', right: 8, top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 0, cursor: 'pointer', color: 'var(--text-muted)',
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            {onDownload && (
              <Button
                variant="outline"
                icon={FileSpreadsheet}
                onClick={onDownload}
                disabled={downloading}
              >
                {downloading ? 'Downloading…' : 'Download'}
              </Button>
            )}
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="table-list flex-1 min-h-0" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="table-list-scroll">
              <table className="table-list-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    {[0, 1, 2, 3, 4].map(i => (
                      <th key={i} className="table-list-th">
                        <div className="table-list-skeleton-bar table-list-skeleton-bar--w-sm" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody aria-busy={true} aria-live="polite">
                  <Skeleton.TableRows cols={['2xs', 'md', 'sm', 'sm', 'xs', 'sm']} />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty state — full centered, no table */}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center flex-1 min-h-0" style={{ minHeight: 240 }}>
            <NoDataFound
              title="No data to preview"
              message={search ? 'No rows match the current search.' : 'This dataset has no rows.'}
            />
          </div>
        )}

        {/* Data table */}
        {hasRows && (
          <div className="table-list flex-1 min-h-0" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="table-list-scroll">
              <table className="table-list-table" style={{ fontSize: 12, minWidth: 'max-content' }}>
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    {pv!.columns.map(c => (
                      <th key={c} className="table-list-th" style={{ whiteSpace: 'nowrap' }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody aria-busy={false} aria-live="polite">
                  {pv!.rows.map((row, ri) => (
                    <tr key={ri} className="table-list-row">
                      <td className="table-list-cell table-list-cell--sno">
                        {pv!.page * pv!.pageSize + ri + 1}
                      </td>
                      {row.map((cell, ci) => (
                        <td key={ci} className="table-list-cell" style={{ whiteSpace: 'nowrap' }}>
                          {typeof cell === 'number' ? cell.toLocaleString() : String(cell ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={pv!.page + 1}
              pageSize={pv!.pageSize}
              totalRecords={pv!.total}
              onPageChange={(np: number) => load(np - 1, pv!.search, pv!.pageSize)}
              onPageSizeChange={(s: number) => { setPageSize(s); load(0, search, s); }}
              recordLabel="rows"
            />
          </div>
        )}

      </Modal.Body>
    </Modal>
  );
};

export default DataPreviewModal;
